import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";

export default function StatementForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [drivers, setDrivers] = useState<{ id: string; fullName: string }[]>([]);
  const [form, setForm] = useState({
    driverId: "",
    periodStart: "",
    periodEnd: "",
    totalTrips: 0,
    totalAmount: 0,
    status: "draft",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axiosInstance.get("/drivers").then((r) => setDrivers(r.data));
    if (id) {
      axiosInstance.get(`/statements/${id}`).then((r) => {
        const d = r.data;
        setForm({
          driverId: d.driverId,
          periodStart: d.periodStart?.slice(0, 10) ?? "",
          periodEnd: d.periodEnd?.slice(0, 10) ?? "",
          totalTrips: d.totalTrips,
          totalAmount: Number(d.totalAmount),
          status: d.status,
          notes: d.notes ?? "",
        });
      });
    }
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const payload = {
        ...form,
        notes: form.notes.trim() || null,
      };
      if (isEdit) await axiosInstance.put(`/statements/${id}`, payload);
      else await axiosInstance.post("/statements", payload);
      navigate("/statements");
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string | string[] } } })
        ?.response?.data?.message;
      setError(Array.isArray(message) ? message.join(", ") : message || "Failed to save statement");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full space-y-6">
      <PageBreadcrumb pageTitle={isEdit ? "Edit Statement" : "Add Statement"} />
      <form onSubmit={handleSubmit} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6 grid w-full grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
        {error && (
          <div className="md:col-span-2 p-4 bg-red-50 border border-red-200 text-red-600 rounded-md">
            {error}
          </div>
        )}
        <div>
          <label className="block text-sm font-medium mb-1">Driver</label>
          <select value={form.driverId} onChange={(e) => setForm({ ...form, driverId: e.target.value })} className="w-full border rounded-lg px-4 py-2" required>
            <option value="">Select driver</option>
            {drivers.map((d) => <option key={d.id} value={d.id}>{d.fullName}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full border rounded-lg px-4 py-2">
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
          </select>
        </div>
        <div><label className="block text-sm font-medium mb-1">Period Start</label><input type="date" value={form.periodStart} onChange={(e) => setForm({ ...form, periodStart: e.target.value })} className="w-full border rounded-lg px-4 py-2" required /></div>
        <div><label className="block text-sm font-medium mb-1">Period End</label><input type="date" value={form.periodEnd} onChange={(e) => setForm({ ...form, periodEnd: e.target.value })} className="w-full border rounded-lg px-4 py-2" required /></div>
        <div><label className="block text-sm font-medium mb-1">Total Trips</label><input type="number" value={form.totalTrips} onChange={(e) => setForm({ ...form, totalTrips: Number(e.target.value) })} className="w-full border rounded-lg px-4 py-2" /></div>
        <div><label className="block text-sm font-medium mb-1">Total Amount (£)</label><input type="number" step="0.01" value={form.totalAmount} onChange={(e) => setForm({ ...form, totalAmount: Number(e.target.value) })} className="w-full border rounded-lg px-4 py-2" required /></div>
        <div className="md:col-span-2"><label className="block text-sm font-medium mb-1">Notes</label><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border rounded-lg px-4 py-2" rows={3} /></div>
        <div className="md:col-span-2 flex justify-end gap-4">
          <button type="button" onClick={() => navigate("/statements")} className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300">Cancel</button>
          <button type="submit" disabled={loading} className="px-4 py-2 bg-brand-500 text-white rounded-lg">{loading ? "Saving..." : "Save"}</button>
        </div>
      </form>
    </div>
  );
}
